import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FORUM_SETTINGS_GET,
  FORUM_SETTINGS_UPDATE,
  FORUM_REGENERATE_MEETINGS,
  FORUM_REGENERATE_TRAININGS,
  CHAPTER_LIST_DROPDOWN,
} from "../../routes/routes";
import { FORUM_SETTINGS_UPDATED } from "../../constant/constant";
import { ValidationMessage } from "../../components/Utils";
import { FullPageLoader } from "../../components/common/Loader";
import { usePermission } from "../../hooks/usePermission";

const FORUM_QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ["bold"],
    [{ list: "bullet" }],
  ],
};
const FORUM_QUILL_FORMATS = ["header", "bold", "list"];

const emptySmtp = {
  mail_from_name: "",
  mail_from_email: "",
  mail_to_email: "",
  smtp_host: "",
  smtp_port: "465",
  smtp_user: "",
  smtp_pass: "",
  smtp_secure: "1",
};

const ForumSettingsEdit = () => {
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("forum-settings-edit");
  const authUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuperAdmin = Number(authUser?.user_type) === 1;

  const [terms, setTerms] = useState({ title: "", content: "" });
  const [privacy, setPrivacy] = useState({ title: "", content: "" });
  const [smtp, setSmtp] = useState(emptySmtp);

  const [savingSection, setSavingSection] = useState("");
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  const [chapterOptions, setChapterOptions] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [regenerateModal, setRegenerateModal] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const [selectedTrainingChapter, setSelectedTrainingChapter] = useState(null);
  const [trainingRegenerateModal, setTrainingRegenerateModal] = useState(false);
  const [regeneratingTraining, setRegeneratingTraining] = useState(false);

  const handleApiError = (err) => {
    if (err.response?.status === 422) {
      setErrors(err.response.data.error || {});
      toast.error(err.response.data.msg);
    } else {
      toast.error(err.response?.data?.msg || "Something went wrong");
    }
  };

  const putSection = (data) => {
    return axios.put(FORUM_SETTINGS_UPDATE, data);
  };

  const saveTerms = () => {
    if (!canEdit) return;
    setSavingSection("terms");
    setErrors({});
    putSection({
      terms_and_conditions: { title: terms.title, content: terms.content },
    })
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          toast.success(FORUM_SETTINGS_UPDATED);
        }
      })
      .catch(handleApiError)
      .finally(() => setSavingSection(""));
  };

  const savePrivacy = () => {
    if (!canEdit) return;
    setSavingSection("privacy");
    setErrors({});
    putSection({
      privacy_policy: { title: privacy.title, content: privacy.content },
    })
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          toast.success(FORUM_SETTINGS_UPDATED);
        }
      })
      .catch(handleApiError)
      .finally(() => setSavingSection(""));
  };

  const saveSmtp = () => {
    if (!canEdit) return;
    setSavingSection("smtp");
    setErrors({});
    putSection({
      smtp_configuration: {
        ...smtp,
        smtp_secure: String(smtp.smtp_secure) === "1" ? "1" : "0",
      },
    })
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          toast.success(FORUM_SETTINGS_UPDATED);
        }
      })
      .catch(handleApiError)
      .finally(() => setSavingSection(""));
  };

  const openRegenerateModal = () => {
    if (!isSuperAdmin || regenerating || regeneratingTraining) return;
    setRegenerateModal(true);
  };

  const regenerateMeetings = () => {
    if (!isSuperAdmin || regenerating) return;
    setRegenerating(true);
    const payload = selectedChapter?.value
      ? { chapter_id: selectedChapter.value }
      : {};
    axios
      .post(FORUM_REGENERATE_MEETINGS, payload)
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          toast.success(res.data.msg || "Upcoming meetings regenerated successfully");
          setRegenerateModal(false);
        }
      })
      .catch(handleApiError)
      .finally(() => setRegenerating(false));
  };

  const openTrainingRegenerateModal = () => {
    if (!isSuperAdmin || regenerating || regeneratingTraining) return;
    setTrainingRegenerateModal(true);
  };

  const regenerateTrainings = () => {
    if (!isSuperAdmin || regeneratingTraining) return;
    setRegeneratingTraining(true);
    const payload = selectedTrainingChapter?.value
      ? { chapter_id: selectedTrainingChapter.value }
      : {};
    axios
      .post(FORUM_REGENERATE_TRAININGS, payload)
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          toast.success(res.data.msg || "Upcoming trainings regenerated successfully");
          setTrainingRegenerateModal(false);
        }
      })
      .catch(handleApiError)
      .finally(() => setRegeneratingTraining(false));
  };

  useEffect(() => {
    setLoading(true);
    const requests = [axios.get(FORUM_SETTINGS_GET)];
    if (isSuperAdmin) {
      requests.push(axios.get(CHAPTER_LIST_DROPDOWN));
    }
    Promise.all(requests)
      .then(([settingsRes, chapterRes]) => {
        if (settingsRes.status === 200 && settingsRes.data.success) {
          const data = settingsRes.data.data || {};
          if (data.terms_and_conditions) {
            setTerms({
              title: data.terms_and_conditions.title || "",
              content: data.terms_and_conditions.content || "",
            });
          }
          if (data.privacy_policy) {
            setPrivacy({
              title: data.privacy_policy.title || "",
              content: data.privacy_policy.content || "",
            });
          }
          if (data.smtp_configuration?.content) {
            const raw = data.smtp_configuration.content;
            setSmtp({
              mail_from_name: raw.mail_from_name || "",
              mail_from_email: raw.mail_from_email || "",
              mail_to_email: raw.mail_to_email || "",
              smtp_host: raw.smtp_host || "",
              smtp_port: raw.smtp_port ? String(raw.smtp_port) : "465",
              smtp_user: raw.smtp_user || "",
              smtp_pass: raw.smtp_pass || "",
              smtp_secure:
                raw.smtp_secure === "1" ||
                raw.smtp_secure === 1 ||
                raw.smtp_secure === true
                  ? "1"
                  : "0",
            });
          }
        }
        if (chapterRes?.status === 200 && chapterRes.data.success) {
          setChapterOptions(chapterRes.data.data || []);
        }
      })
      .catch(handleApiError)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="content-wrapper">
      <FullPageLoader visible={loading || !!savingSection || regenerating || regeneratingTraining} />
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Forum Settings</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">Forum Settings</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow-1">
        <div className="row">
          <div className="col-xl-12">
            {/* Terms and Conditions */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}>
                  Terms and Conditions
                </h5>
              </div>
              <div className="card-body app-form-fields">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveTerms();
                  }}
                >
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Content<span className="text-danger">*</span>
                        </label>
                        <ReactQuill
                          className="forum-legal-editor"
                          theme="snow"
                          readOnly={!canEdit}
                          modules={FORUM_QUILL_MODULES}
                          formats={FORUM_QUILL_FORMATS}
                          value={terms.content}
                          onChange={(content) =>
                            setTerms({ ...terms, content })
                          }
                        />
                        <ValidationMessage error={errors} name="content" />
                      </div>
                    </div>
                    {canEdit && (
                      <div className="demo-inline-spacing d-flex justify-content-end">
                        <button
                          disabled={savingSection === "terms"}
                          type="submit"
                          className="btn btn-success isubmit_button"
                        >
                          {savingSection === "terms"
                            ? "Saving..."
                            : "Save Terms"}
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Privacy Policy */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}>
                  Privacy Policy
                </h5>
              </div>
              <div className="card-body app-form-fields">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    savePrivacy();
                  }}
                >
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label className="form-label">
                          Content<span className="text-danger">*</span>
                        </label>
                        <ReactQuill
                          className="forum-legal-editor"
                          theme="snow"
                          readOnly={!canEdit}
                          modules={FORUM_QUILL_MODULES}
                          formats={FORUM_QUILL_FORMATS}
                          value={privacy.content}
                          onChange={(content) =>
                            setPrivacy({ ...privacy, content })
                          }
                        />
                        <ValidationMessage error={errors} name="content" />
                      </div>
                    </div>
                    {canEdit && (
                      <div className="demo-inline-spacing d-flex justify-content-end">
                        <button
                          disabled={savingSection === "privacy"}
                          type="submit"
                          className="btn btn-success isubmit_button"
                        >
                          {savingSection === "privacy"
                            ? "Saving..."
                            : "Save Privacy Policy"}
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* SMTP Configuration */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}>
                  SMTP Configuration
                </h5>
              </div>
              <div className="card-body app-form-fields">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveSmtp();
                  }}
                >
                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="mail_from_name">
                          From Name<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="mail_from_name"
                          placeholder="From Name"
                          disabled={!canEdit}
                          value={smtp.mail_from_name}
                          onChange={(e) =>
                            setSmtp({
                              ...smtp,
                              mail_from_name: e.target.value,
                            })
                          }
                        />
                        <ValidationMessage
                          error={errors}
                          name="mail_from_name"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="mail_from_email">
                          From Email<span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="mail_from_email"
                          placeholder="From Email"
                          disabled={!canEdit}
                          value={smtp.mail_from_email}
                          onChange={(e) =>
                            setSmtp({
                              ...smtp,
                              mail_from_email: e.target.value,
                            })
                          }
                        />
                        <ValidationMessage
                          error={errors}
                          name="mail_from_email"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="mail_to_email">
                          To Email<span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="mail_to_email"
                          placeholder="To Email"
                          disabled={!canEdit}
                          value={smtp.mail_to_email}
                          onChange={(e) =>
                            setSmtp({ ...smtp, mail_to_email: e.target.value })
                          }
                        />
                        <ValidationMessage
                          error={errors}
                          name="mail_to_email"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="smtp_host">
                          SMTP Host<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="smtp_host"
                          placeholder="SMTP Host"
                          disabled={!canEdit}
                          value={smtp.smtp_host}
                          onChange={(e) =>
                            setSmtp({ ...smtp, smtp_host: e.target.value })
                          }
                        />
                        <ValidationMessage error={errors} name="smtp_host" />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="smtp_port">
                          SMTP Port<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="smtp_port"
                          placeholder="465"
                          disabled={!canEdit}
                          value={smtp.smtp_port}
                          onChange={(e) =>
                            setSmtp({ ...smtp, smtp_port: e.target.value })
                          }
                        />
                        <ValidationMessage error={errors} name="smtp_port" />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="smtp_user">
                          SMTP Username<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="smtp_user"
                          placeholder="SMTP Username"
                          disabled={!canEdit}
                          value={smtp.smtp_user}
                          onChange={(e) =>
                            setSmtp({ ...smtp, smtp_user: e.target.value })
                          }
                        />
                        <ValidationMessage error={errors} name="smtp_user" />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="smtp_pass">
                          SMTP Password<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="smtp_pass"
                          placeholder="SMTP Password"
                          disabled={!canEdit}
                          value={smtp.smtp_pass}
                          onChange={(e) =>
                            setSmtp({ ...smtp, smtp_pass: e.target.value })
                          }
                          autoComplete="off"
                        />
                        <ValidationMessage error={errors} name="smtp_pass" />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <div className="form-check mt-4">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="smtp_secure"
                            disabled={!canEdit}
                            checked={String(smtp.smtp_secure) === "1"}
                            onChange={(e) =>
                              setSmtp({
                                ...smtp,
                                smtp_secure: e.target.checked ? "1" : "0",
                              })
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="smtp_secure"
                          >
                            Use SSL/TLS (secure)
                          </label>
                        </div>
                        <ValidationMessage error={errors} name="smtp_secure" />
                      </div>
                    </div>
                    {canEdit && (
                      <div className="demo-inline-spacing d-flex justify-content-end">
                        <button
                          disabled={savingSection === "smtp"}
                          type="submit"
                          className="btn btn-success isubmit_button"
                        >
                          {savingSection === "smtp"
                            ? "Saving..."
                            : "Save SMTP Configuration"}
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {isSuperAdmin && (
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0" style={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}>
                    Meeting Schedule
                  </h5>
                </div>
                <div className="card-body app-form-fields">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="regenerate_chapter_id">
                          Circle
                        </label>
                        <Select
                          inputId="regenerate_chapter_id"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          name="chapter_id"
                          isClearable
                          placeholder="All Circles"
                          options={chapterOptions}
                          value={selectedChapter}
                          onChange={(selected) => setSelectedChapter(selected)}
                        />
                        <small className="text-muted d-block mt-2">
                          Select a circle to regenerate only that circle. Leave empty to regenerate upcoming meetings for all launched circles.
                        </small>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="demo-inline-spacing d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-success isubmit_button"
                          disabled={regenerating || regeneratingTraining}
                          onClick={openRegenerateModal}
                        >
                          {regenerating ? "Regenerating..." : "Regenerate Upcoming Meetings"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {isSuperAdmin && (
              <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0" style={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}>
                    Training Schedule
                  </h5>
                </div>
                <div className="card-body app-form-fields">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label" htmlFor="regenerate_training_chapter_id">
                          Circle
                        </label>
                        <Select
                          inputId="regenerate_training_chapter_id"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          name="training_chapter_id"
                          isClearable
                          placeholder="All Circles"
                          options={chapterOptions}
                          value={selectedTrainingChapter}
                          onChange={(selected) => setSelectedTrainingChapter(selected)}
                        />
                        <small className="text-muted d-block mt-2">
                          Select a circle to regenerate only that circle. Leave empty to regenerate upcoming training for all circles with training schedule configured.
                        </small>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="demo-inline-spacing d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-success isubmit_button"
                          disabled={regenerating || regeneratingTraining}
                          onClick={openTrainingRegenerateModal}
                        >
                          {regeneratingTraining ? "Regenerating..." : "Regenerate Upcoming Trainings"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        show={regenerateModal}
        onHide={() => !regenerating && setRegenerateModal(false)}
        centered
      >
        <Modal.Header closeButton={!regenerating}>
          <Modal.Title>Regenerate Meetings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedChapter?.label
            ? `Regenerate upcoming meetings for ${selectedChapter.label}?`
            : "Regenerate upcoming meetings for all launched circles?"}
          <div className="text-muted mt-2" style={{ fontSize: "0.875rem" }}>
            Past meetings will not be changed. Only upcoming meetings and fees will be regenerated.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary ibutton"
            disabled={regenerating}
            onClick={() => setRegenerateModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success isubmit_button"
            disabled={regenerating}
            onClick={regenerateMeetings}
          >
            {regenerating ? "Regenerating..." : "Confirm"}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={trainingRegenerateModal}
        onHide={() => !regeneratingTraining && setTrainingRegenerateModal(false)}
        centered
      >
        <Modal.Header closeButton={!regeneratingTraining}>
          <Modal.Title>Regenerate Trainings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTrainingChapter?.label
            ? `Regenerate upcoming training for ${selectedTrainingChapter.label}?`
            : "Regenerate upcoming training for all circles with training schedule configured?"}
          <div className="text-muted mt-2" style={{ fontSize: "0.875rem" }}>
            Past trainings will not be changed. Manually rescheduled trainings are kept.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary ibutton"
            disabled={regeneratingTraining}
            onClick={() => setTrainingRegenerateModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success isubmit_button"
            disabled={regeneratingTraining}
            onClick={regenerateTrainings}
          >
            {regeneratingTraining ? "Regenerating..." : "Confirm"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ForumSettingsEdit;
